import {
  RawAesWrappingSuiteIdentifier,
  RawAesKeyringWebCrypto,
  buildClient as buildClientBrowser,
  MultiKeyringWebCrypto,
} from '@aws-crypto/client-browser';
import assert from 'assert';

import BenchlingConfig from 'BenchlingConfig';
import {EncryptedRealtimeMessage, RealtimeMessage} from 'realtime/RealtimeChannel';

import {RealtimeEncryptionKey} from './RealtimeEncryptionReduxActions';

/**
 * Lite version of Python `benchling_crypto.key_refs.SharedSecret` that only deserializes.
 *
 * Assumption is that the client has a right to use the key material, but try to avoid logging it!
 */
class BenchlingCryptoSharedSecretLite {
  public readonly id: string;
  public readonly keyMaterial: Uint8Array;

  constructor(id: string, keyMaterial: Uint8Array) {
    if (id.length === 0) {
      throw new Error(`Invalid key id: ${id}`);
    }
    if (keyMaterial.length !== 32) {
      throw new Error('Invalid key material');
    }
    this.id = id;
    this.keyMaterial = keyMaterial;
  }

  /** Parse shared secret from benchling-crypto serialization format. */
  public static parse(serialized: string): BenchlingCryptoSharedSecretLite {
    const [label, id, keyMaterial] = serialized.split(':');
    if (label !== 'secret') {
      throw new Error(`Invalid secret key label: ${label}`);
    }
    // Translate from base64url to Uint8Array
    const b64: string = keyMaterial.replaceAll('-', '+').replaceAll('_', '/');
    // Leave ID as-is
    return new BenchlingCryptoSharedSecretLite(
      id,
      Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
    );
  }

  /**
   * Redacted serialization of the key.
   *
   * Intended to prevent _accidental_ logging.
   */
  public toString(): string {
    return `secret:${this.id}:**REDACTED**`;
  }

  public toJSON(): string {
    return this.toString();
  }
}

export async function decryptMessageIfNeeded(
  channelName: string,
  message: RealtimeMessage | EncryptedRealtimeMessage,
  encryptionKeys: Array<RealtimeEncryptionKey>,
  // To enable more realistic unit testing in a Node.js environment allow these awsCrypto dependencies to be
  // injected which let's us swap over to @aws-crypto/client-node for testing.
  // First argument should be of type RawAesKeyringNode
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  _internalRawAesKeyringCrypto: any | null = null,
  // Second argument should be of type buildClient from @aws-crypto/client-node
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  _internalDecryptionClient: any | null = null,
  // Third argument should be of type MultiKeyringNode
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  _internalMultiKeyringNode: any | null = null
): Promise<RealtimeMessage> {
  // eslint-disable-next-line benchlint/no-in-operator
  if (!('ciphertext' in message.data) || message.data.ciphertext == null) {
    return message as RealtimeMessage;
  }

  if (!encryptionKeys || encryptionKeys.length === 0) {
    throw new Error('No encryption keys provided for decryption');
  }

  const {ciphertext} = message.data;
  assert(ciphertext, 'Message data must contain ciphertext for decryption');
  const ciphertextBuffer = Uint8Array.from(atob(ciphertext), (c) => c.charCodeAt(0));

  const keys = encryptionKeys.map((key) => BenchlingCryptoSharedSecretLite.parse(key.key));

  const keyNamespace = 'bnch-ably';
  const wrappingSuite = RawAesWrappingSuiteIdentifier.AES256_GCM_IV12_TAG16_NO_PADDING;

  // Create a multi-keyring that contains all individual keyrings
  // The AWS crypto library will automatically try each keyring during decryption
  // Decrypt once with the multi-keyring containing all keys
  let plaintext;
  let messageHeader;
  // Create individual keyrings for each key
  const keyrings = [];
  if (global.__NODE_TESTING__) {
    for (const key of keys) {
      // For Node.js testing environment
      keyrings.push(
        new _internalRawAesKeyringCrypto({
          keyName: key.id,
          keyNamespace,
          wrappingSuite,
          unencryptedMasterKey: key.keyMaterial,
        })
      );
    }
    const multiKeyring = new _internalMultiKeyringNode({
      generator: keyrings[0], // Use first keyring as generator
      children: keyrings.slice(1), // Rest as children
    });
    try {
      ({plaintext, messageHeader} = await _internalDecryptionClient.decrypt(multiKeyring, ciphertextBuffer));
    } catch (err) {
      throw new Error('Keys unable to decrypt messages');
    }
  } else {
    for (const key of keys) {
      // For browser environment
      const masterKey = await RawAesKeyringWebCrypto.importCryptoKey(key.keyMaterial, wrappingSuite);
      keyrings.push(
        new RawAesKeyringWebCrypto({
          keyName: key.id,
          keyNamespace,
          wrappingSuite,
          masterKey,
        })
      );
    }
    const internalDecryptionClient = buildClientBrowser();
    // For browser environment
    const multiKeyring = new MultiKeyringWebCrypto({
      generator: keyrings[0], // Use first keyring as generator
      children: keyrings.slice(1), // Rest as children
    });
    try {
      ({plaintext, messageHeader} = await internalDecryptionClient.decrypt(multiKeyring, ciphertextBuffer));
    } catch (err) {
      throw new Error('Keys unable to decrypt messages');
    }
  }

  // Check the context to ensure the message was intended for us (encryption
  // context is plaintext and authenticated, see benchling-crypto docs). DO NOT
  // return the message _unless_ the context is valid.
  const {encryptionContext} = messageHeader;
  assert(
    encryptionContext.channel === channelName,
    "Encryption context channel doesn't match expected channel name"
  );

  assert(
    encryptionContext.tenant === BenchlingConfig.TENANT_ID,
    "Encryption context tenant ID doesn't match expected tenant ID"
  );

  const decryptedText = new TextDecoder().decode(plaintext);
  return {name: message.name, data: JSON.parse(decryptedText)};
}
